import React, { FC, useState, useCallback } from 'react'
import Pagination from './Pagination'
import 'sortable-table-pattern/dist/main.css'

// Import the sortable table initialization function
import sortableTableInit from 'sortable-table-pattern/src/sortable-table'

// Only access `document` in browser environments (prevents errors in Node tests)
if (
  typeof document !== 'undefined' &&
  document.onreadystatechange !== undefined
) {
  document.onreadystatechange = function () {
    if (document.readyState === 'complete') {
      sortableTableInit()
    }
  }
}

interface TableColumn {
  key: string
  label: string
  className?: string
  icon?: string
  isSortable?: boolean
  sortKey?: string // Key to use for sorting, if different from display key
}

interface TableRow {
  [key: string]: any
  _totalRow?: boolean
  _rowStyle?: React.CSSProperties
}

interface PaginationProps {
  totalItems: number
  itemsPerPage: number
}

interface TableProps {
  columns: TableColumn[]
  rows: TableRow[]
  onActionClick?: (rowData: TableRow) => void
  showPagination?: boolean
  paginationProps?: PaginationProps
  caption?: string
  isSorting?: boolean
  currentPage?: number
  onPageChange?: (page: number) => void
  uploadDocumentMobileOnly?: boolean
  managerPortalMobileOnly?: boolean
  onSort?: (columnKey: string, sortDirection: 'ASC' | 'DESC') => void
  sortColumn?: string
  sortDirection?: 'ASC' | 'DESC'
  /** When true, the thead row sticks to the top of the nearest scrolling container. */
  stickyHeader?: boolean
  renderCell?: (row: TableRow, column: TableColumn) => React.ReactNode
}

const Table: FC<TableProps> = ({
  columns,
  rows,
  onActionClick,
  showPagination = false,
  paginationProps,
  caption,
  isSorting = false,
  currentPage: controlledPage,
  onPageChange,
  uploadDocumentMobileOnly = false,
  managerPortalMobileOnly = false,
  onSort,
  sortColumn,
  sortDirection,
  stickyHeader = false,
  renderCell,
}) => {
  const [internalPage, setInternalPage] = useState(1)

  const handleSort = useCallback(
    (columnKey: string, sortKey?: string) => {
      if (!onSort) return

      // Use provided sortKey or fall back to columnKey
      const keyToSort = sortKey || columnKey

      // Default sort is ASC; the first click on a column (or a newly clicked
      // column) sorts DESC, and further clicks on the same column toggle back to ASC.
      const newDirection =
        sortColumn === keyToSort && sortDirection === 'DESC' ? 'ASC' : 'DESC'

      onSort(keyToSort, newDirection)
    },
    [onSort, sortColumn, sortDirection]
  )

  const currentPage = controlledPage ?? internalPage
  const isServerSidePagination = !!onPageChange // Server-side pagination when onPageChange is provided

  const usePagination = showPagination && paginationProps
  const itemsPerPage = usePagination
    ? paginationProps.itemsPerPage
    : rows.length
  const totalItems = usePagination ? paginationProps.totalItems : rows.length

  // For server-side pagination, display all rows as-is (API already paginated)
  // For client-side pagination, slice rows locally
  let visibleRows = rows
  if (usePagination && !isServerSidePagination) {
    const startIndex = (currentPage - 1) * itemsPerPage
    const endIndex = startIndex + itemsPerPage
    visibleRows = rows.slice(startIndex, endIndex)
  }

  return (
    <div className="flex flex-col w-full">
      <div
        className={`${stickyHeader ? '' : 'overflow-x-auto'}`}
        style={{ WebkitOverflowScrolling: 'touch' }}
      >
        <table
          className={`data-table !mt-0 ds-data-table--stripes-odd ${isSorting ? 'ds-data-table--sortable' : ''}`}
          data-ds-variant="mobile"
        >
          <caption
            className={`${uploadDocumentMobileOnly ? 'mobile:!mb-0' : ''} ${managerPortalMobileOnly ? 'mobile:!mt-[16px] ' : ''}`}
          >
            <p className="--medium">{caption}</p>
          </caption>

        {uploadDocumentMobileOnly && (
          <div className="tablet:!hidden desktop:!hidden">
            <hr className="-variation-1  !mb-0 " />
          </div>
        )}

        <thead>
          <tr>
            {columns.map((col, index) => {
              // Use sortKey if provided (for sorting by different field), otherwise use column key
              const sortFieldKey = col.sortKey || col.key

              // Determine if this column is currently sorted
              const isCurrentlySorted = sortColumn === sortFieldKey

              // Set aria-sort attribute for accessibility
              let ariaSort: 'ascending' | 'descending' | undefined = undefined
              if (isCurrentlySorted) {
                ariaSort = sortDirection === 'ASC' ? 'ascending' : 'descending'
              }

              // Chevron reflects sort state: up by default and for ASC, down only once this column is sorted DESC
              const chevronIcon =
                isCurrentlySorted && sortDirection === 'DESC'
                  ? '/assets/images/chevron-down-outline.svg'
                  : '/assets/images/chevron-up-outline.svg'

              return (
                <th
                  key={col.key}
                  scope="col"
                  className={`${col.className ?? ''} ${uploadDocumentMobileOnly ? 'pl-0! pr-0!' : ''} ${stickyHeader ? 'sticky! top-0! z-[1]! bg-white!' : ''}`}
                  aria-sort={ariaSort}
                >
                  {col.isSortable && onSort ? (
                    // Sortable column header - clickable button
                    <button
                      type="button"
                      className="th-btn -medium flex items-center gap-2"
                      data-posinset={index + 1}
                      data-setsize={columns.length}
                      data-direction={
                        isCurrentlySorted ? sortDirection : undefined
                      }
                      data-text={col.label}
                      onClick={() => handleSort(col.key, col.sortKey)}
                    >
                      <span className="whitespace-pre-wrap flex-1">{col.label}</span>
                      <img
                        className="arrows-table w-5 h-5 flex-shrink-0"
                        src={chevronIcon}
                        alt="sort indicator"
                        height="20"
                        width="20"
                      />
                    </button>
                  ) : (
                    // Non-sortable column header
                    <span className="whitespace-pre-wrap">{col.label}</span>
                  )}
                </th>
              )
            })}
          </tr>
        </thead>

        <tbody>
          {visibleRows.map((row, rowIndex) => {
            const isLastRow = rowIndex === visibleRows.length - 1

            // Build className based on conditions
            let trClassName = ''
            if (uploadDocumentMobileOnly) {
              trClassName += 'border-b-0!   pl-0! pr-0!'
            }
            if (managerPortalMobileOnly) {
              const borderClass = isLastRow ? '' : 'border-b-0!'
              trClassName += ` ${borderClass} border-t! border-t-[#878F9B]!`
            }

            return (
              <tr
                key={row.id as string}
                className={trClassName}
                style={row._rowStyle}
              >
                {columns.map((col) => {
                  const cellContent = renderCell
                    ? renderCell(row, col)
                    : row[col.key]

                  return (
                    <td
                      key={`${row.id}-${col.key}`} className={`text-[14px]! ${col.className ?? ""} ${row._totalRow ? 'p3 -medium' : ''}`}
                      style={row._totalRow ? { fontWeight: 'bold' } : undefined}
                    >
                      <span className="mobile-only table-head">
                        {col.label}
                      </span>

                      {col.key === 'action' && onActionClick ? (
                        <button type="button"
                          className="-medium"
                          data-analytics-type="link"
                          data-analytics-click="General click"
                          onClick={() => {
                            onActionClick(row)
                          }}
                        >
                          {row.action}
                        </button>
                      ) : (
                        cellContent
                      )}
                    </td>
                  )
                })}
              </tr>
            )
          })}
        </tbody>
      </table>
      </div>

      {usePagination && (
        <div className="mt-4">
          <Pagination
            currentPage={currentPage}
            totalItems={totalItems}
            itemsPerPage={itemsPerPage}
            onPageChange={onPageChange ?? setInternalPage}
          />
        </div>
      )}
    </div>
  )
}

export default Table
